import { Select } from "antd";
import { DefaultOptionType } from "antd/es/select";
import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import { getEditorInfo } from "src/common/attrEdit";
import { AttrPersonSelectProps } from "src/common/attrEdit/components/ZAttrPersonSelect";
import { objectModelVersionStore } from "src/common/objectModelVersionStore";
import { onError } from "src/common/onError";
import { ZAttribute } from "src/types/ZAttribute";
import { ZUid } from "src/types/zUid";
import { loadPersonsOptions } from "./loadPersonsOptions";

const usersCache: Record<string, Promise<DefaultOptionType[]>> = {};

const makeOptionsCacheKey = (attribute: ZAttribute, objectId: ZUid) => {
  const editor = getEditorInfo(attribute.viewStyles);
  const component =
    editor?.component?.editor === "PersonSelect" ? editor.component : null;
  return [
    objectId,
    (attribute.roleIds ?? []).join("-"),
    component?.separator ?? "comma",
    JSON.stringify(component?.rolesViewAtts ?? {}),
    objectModelVersionStore.version ?? "",
  ].join("|");
};

const getOptions = async (attribute: ZAttribute, objectId: ZUid) => {
  const key = makeOptionsCacheKey(attribute, objectId);
  const val = usersCache[key];
  if (val) return val;
  const editor = getEditorInfo(attribute.viewStyles);
  if (editor?.component?.editor !== "PersonSelect") return [];
  const { rolesViewAtts } = editor.component;
  const roleIds = attribute.roleIds || [];
  const newVal = loadPersonsOptions(
    roleIds,
    objectId,
    undefined,
    rolesViewAtts ?? undefined,
    editor?.component.separator || "comma",
  );
  usersCache[key] = newVal;

  return newVal;
};

export type PropsPersonSelect = AttrPersonSelectProps & {
  value?: string[] | null;
  onChange?(newValue?: string[] | null): void;
  attribute: ZAttribute;
  objectId: ZUid;
  disabled?: boolean;
  allowClear?: boolean;
  multiple?: boolean;
};

export const PersonSelect: React.FC<PropsPersonSelect> = observer(
  ({
    value,
    onChange,
    disabled = false,
    attribute,
    multiple,
    allowClear = false,
    objectId,
  }) => {
    const [loading, setLoading] = useState(false);
    const [options, setOptions] = useState<DefaultOptionType[]>([]);

    const init = async () => {
      setLoading(true);

      getOptions(attribute, objectId)
        .then((data) => {
          setOptions(data);
        })
        .finally(() => setLoading(false))
        .catch(onError);
    };
    useEffect(() => {
      init();
    }, [attribute.viewStyles, objectId, attribute.roleIds]);

    const handleChange = (newValue?: string[] | null) => {
      onChange?.(newValue ?? []);
    };

    return (
      <Select
        value={value}
        loading={loading}
        options={options}
        showSearch
        disabled={disabled}
        mode={multiple ? "multiple" : undefined}
        allowClear={allowClear}
        // onChange={onChange}
        onChange={handleChange}
        filterOption={(input, option) =>
          option?.searchText.indexOf(input.toLowerCase()) >= 0
        }
      />
    );
  },
);
